import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { PaletteLike } from "@picnic-field/renderer/appearance";
import type { RunBundle } from "@picnic-field/local-host/protocol";
import { toApiError } from "../../../lib/http/errors";
import type { ErrorCode } from "../../../lib/http/errors";
import type { ReplayInfo } from "../../../lib/api/socialApi";
import { useSocialApi } from "../../../lib/api/socialApi";
import { useFieldApi } from "../../../hooks/useFieldApi";
import { Button } from "../../../components/ui/Button";
import { FeBanner } from "../../../components/FeBanner";
import { PicoBubble } from "../../../components/PicoBubble";
import { ScreenRoot, ToastHost, useToasts } from "../../../components/Shell";
import { useTopBar } from "../../../components/TopBar";
import { useCourseApi } from "../../../lib/api/courseApi";
import type { GalleryViewLike, GalleryViewRequest } from "../../course";
import { ROOM_SCENE } from "../../course";
import { formatClock } from "../../course";
import { useDensity } from "../../../store/density";
import { useI18n } from "../../../i18n";
import { relayFocus, sectionsOf } from "../../room";
import type { Focus } from "../../room";
import { expandMeta } from "../../room";
import type { RoomMeta } from "../../room";
import { roomDeps } from "../../room";
import { useTeacherApi } from "../../teacher-settings";
import { useVideoApi } from "../../video";
import { ReplayVideoPanel } from "../../video";
import { SPEEDS, fraction, keySeek, showTimes, tickAt, tickToS } from "../lib/logic";
import type { Speed } from "../lib/logic";
import { openRoomPlayer, relayBar, roomAnalysis, stageStartTick } from "../lib/roomReplay";
import { withServerFallback, type FallbackPlayer, type ServerState } from "../lib/serverReplay";
import { track } from "../../../lib/telemetry/telemetry";
import "../../../styles/course.css";
import "../../../styles/relay.css";
import "../../../styles/replay.css";
import type {
  MachineLabels,
  PlaybackVariant,
  ObserverSource,
  RoomAnalysis,
  RoomTimeline,
} from "../types";
import { ZoomControls } from "../../../components/ZoomControls";

/** 한 프레임에 최대 몇 틱까지 따라잡나(느린 기기에서 밀리면 늦게 가더라도 멈추지 않게) */
/**
 * 한 프레임에 시뮬에 쓸 시간(ms) · 밀린 틱 상한 — 틱 수가 아니라 시간으로 자른다.
 * 그리기가 느린 기기(GPU 없음 · 초당 몇 프레임)에서도 시뮬은 가벼워 실시간을 따라간다(틱 수 상한 8 이면 초당 8틱 = 1/8 속도였다).
 */
const STEP_BUDGET_MS = 24;
const MAX_BACKLOG_TICKS = 240;
/** 구간 정보가 없거나 Setup · 완성일 때 전체를 보는 자리 */
const OVERVIEW: Focus = { x: 0, z: 0, back: 20 };

type Load =
  | { status: "loading" }
  | { status: "error"; code: ErrorCode }
  | { status: "unreadable" }
  | {
      status: "ready";
      player: FallbackPlayer;
      palette: PaletteLike | null;
      analysis: RoomAnalysis;
    };

/** 재생 방식 요약(상단 칩 · 안내) — 재생 루프가 바뀔 때만 올린다 */
interface PlaybackInfo {
  mismatch: number | null;
  playback: "input" | "server";
  server: ServerState;
  /** 재생 전 전환(빌드가 다름 — SR-REC-02) */
  build: boolean;
}

/**
 * FS-14-01 룸 권위 리플레이 재생(P3-S3 · SCR v0.6 sfs-14 · v0.7 검토 3-5) — room-replay@1 을 room-sim 재생기로 다시 돌려
 * 셸 룸 렌더 경로(expandMeta → 전시관 뷰)로 그린다. 재생 · 일시정지 · 배속 0.5/1/2 · 탐색(앞으로는 이어서 · 뒤로는 처음부터 재시뮬).
 *  - 릴레이: 구간 진행 막대(누르면 그 구간으로) · 지금 구간 라벨 — 교사만 별명, 학생은 '구간 n'(DR-01) · 실패·재시도 표시 없음
 *    · 카메라는 지금 구간(renderer sectionFocus)
 *  - 교사: 상단 칩 '권위 리플레이' · '해시 ✓/✕ · 입력 재생' · 오른쪽 FS-14-02 영상 페인 · 시간 표시
 *  - 체크포인트 해시가 어긋나면(mismatch · FE-501) 그 틱부터 **서버 재생**(SR-REC-02 · ./lib/serverReplay — 서버가 다시 돌린 자세 조각을 스트리밍)
 *    · 교사: 재생 방식 칩 '서버 재생 · 권위(PH2)' 켜짐 + FE-501 안내 · 학생: '다시 맞춰서 재생해요'. 서버를 쓸 수 없으면 입력 재생을 이어 간다.
 *    관찰자(공유 링크)는 서버 재생 경로가 없어 입력 재생만.
 *  - 2026-09-28: 머리 versions(기록한 룸 서버 빌드)가 이 기기 빌드와 다르면 **재생 전에** 서버 재생(틱 0부터) — 교사 안내
 *    '기록한 판과 달라 처음부터 서버가 다시 돌린 기록' · 학생은 같은 '다시 맞춰서 재생해요'. 빌드를 모르는 옛 리플레이는 해시로만.
 */
export function RoomReplayView({
  info,
  teacher,
  observer,
}: {
  info: ReplayInfo;
  teacher: boolean;
  observer?: ObserverSource;
}) {
  const { t } = useI18n();
  const { density } = useDensity();
  const social = useSocialApi();
  const courseApi = useCourseApi();
  const fieldApi = useFieldApi();
  const teacherApi = useTeacherApi();
  const videoApi = useVideoApi();
  const toasts = useToasts();
  const [load, setLoad] = useState<Load>({ status: "loading" });
  const [seq, setSeq] = useState(0);
  const [course, setCourse] = useState<string | null>(null);
  const [names, setNames] = useState<ReadonlyMap<string, string>>(new Map());
  const [pb, setPb] = useState<PlaybackInfo>({
    mismatch: null,
    playback: "input",
    server: "off",
    build: false,
  });

  // ── 바이트 · 묶음 · 런타임 설정 → 재생기 ──
  useEffect(() => {
    if ((!social && !observer) || !courseApi || !fieldApi) return;
    let alive = true;
    let opened: FallbackPlayer | null = null;
    let analysis: RoomAnalysis | null = null;
    setLoad({ status: "loading" });
    (async () => {
      const meta = (info.meta ?? {}) as { machines?: { snapshotId?: unknown }[] };
      const ids = new Set(info.snapshotIds);
      for (const m of meta.machines ?? [])
        if (typeof m.snapshotId === "string" && m.snapshotId) ids.add(m.snapshotId);
      const [bytes, config, bundles] = await Promise.all([
        observer ? observer.file() : social!.replayFile(info.replayId),
        courseApi.runtimeConfig(),
        Promise.all(
          [...ids].map(async (snapshotId) => ({
            snapshotId,
            // P3-S6 리플레이 경로 묶음 — 교류 룸 상대 반 기계는 코스 run-bundle 경로로 못 받는다(404)
            bundle: (observer
              ? await observer.bundle(snapshotId)
              : await social!.replayBundle(info.replayId, snapshotId)) as RunBundle,
          })),
        ),
      ]);
      // 작품마다 제 appearance.palette 이름의 팔레트(2026-09-30 팔레트 버전 전환)
      const palette = await fieldApi.palettes(bundles.map((b) => b.bundle?.appearance?.palette));
      const base = await openRoomPlayer({
        bytes,
        bundles,
        runtimeConfig: config,
        fetch: (url: string) => fetch(url),
      });
      // SR-REC-02 — 해시가 어긋나면 서버 재생으로(관찰자는 경로가 없어 입력 재생만)
      const player = withServerFallback(
        base,
        !observer && social
          ? (from, to) => social.replayServerTrack(info.replayId, from, to)
          : null,
        {
          onSwitch: (_tick, why) => {
            track({
              t: "playback",
              code: "server",
              detail: why === "build" ? "room-replay-build" : "room-replay",
            });
            // 빌드가 달라 재생 전에 넘어간 것은 해시를 본 것이 아니다 — 불일치로 보고하지 않는다
            if (why === "hash") void social?.reportHash(info.replayId, false);
          },
        },
      );
      opened = player;
      if (!alive) return player.dispose();
      // P3-S6 구간 분석은 Worker 에서 곧바로 시작(장면을 싣는 동안 — 못 쓰면 메인 스레드 대체)
      analysis = roomAnalysis(base, { bytes, bundles, runtimeConfig: config });
      if (player.mode.kind === "relay") analysis.timeline().catch(() => {});
      setLoad({ status: "ready", player, palette, analysis });
    })().catch((e: unknown) => {
      if (!alive) return;
      const code = (e as { code?: unknown })?.code;
      // 재생기 오류(형식 · 묶음 없음 · 릴레이 재구성 불일치) — 볼 수 없는 리플레이
      if (typeof code === "string" && !/^(FE|NET|HTTP)-/.test(code))
        setLoad({ status: "unreadable" });
      else setLoad({ status: "error", code: toApiError(e).code });
    });
    return () => {
      alive = false;
      analysis?.dispose();
      opened?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- observer 는 바이트·묶음 경로만(라벨이 바뀌어도 다시 싣지 않는다)
  }, [info, social, courseApi, fieldApi, seq, !observer]);

  // 교사 — 반 이름(제목) · 별명(pid → 별명: 구간 라벨)
  useEffect(() => {
    if (!teacher || !fieldApi) return;
    let alive = true;
    fieldApi
      .teacherOverview()
      .then((d) => alive && setCourse(d?.courseLabel ?? null))
      .catch(() => {});
    teacherApi
      ?.students()
      .then((xs) => alive && setNames(new Map(xs.map((s) => [s.pid, s.nickname]))))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [teacher, fieldApi, teacherApi]);

  const player = load.status === "ready" ? load.player : null;
  const variant: PlaybackVariant = observer ? "observer" : teacher ? "teacher" : "student";
  const labels: MachineLabels = useMemo(() => {
    if (observer) return observer.labels;
    const m = new Map<number, { label: string; mine: boolean }>();
    if (!teacher || !player) return m;
    for (const x of player.machines) {
      const n = x.pid ? names.get(x.pid) : undefined;
      if (n) m.set(x.machine, { label: n, mine: false });
    }
    return m;
  }, [observer, teacher, player, names]);
  // 관찰자 화면은 상단 바를 공유 화면(FS-01-05)이 정한다
  const top = observer ? null : (
    <RoomReplayTop
      kind={player?.mode.kind ?? "relay"}
      course={course}
      teacher={teacher}
      mismatch={pb.mismatch}
      playback={pb.playback}
      build={pb.build}
    />
  );
  const audience = teacher || observer ? "teacher" : "student";

  if (load.status === "error")
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        {top}
        <FeBanner
          code={load.code}
          audience={audience}
          actions={[{ label: t("common.retry"), icon: "⟲", onClick: () => setSeq((n) => n + 1) }]}
        />
      </ScreenRoot>
    );
  if (load.status === "unreadable")
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        {top}
        <div className="pf-pane pf-pane--hero" data-testid="replay-unreadable">
          <PicoBubble text={t("replay.unreadable")} live={false} />
        </div>
      </ScreenRoot>
    );
  if (!player)
    return (
      <ScreenRoot id="FS-14-01" title={t("replay.title")} hideTitle>
        {top}
        <div className="pf-pane" aria-busy="true">
          <PicoBubble text={t("replay.loading")} busy />
        </div>
      </ScreenRoot>
    );

  return (
    <ScreenRoot
      id="FS-14-01"
      title={t("replay.title")}
      hideTitle
      fill
      className={`pf-replay pf-replay--room pf-replay--${density} pf-course--${density}${teacher ? " pf-replay--teacher" : ""}${observer ? " pf-replay--observer" : ""}`}
    >
      {top}
      <div className="pf-replay__main">
        <RoomPlayback
          player={player}
          analysis={load.status === "ready" ? load.analysis : null}
          palette={load.status === "ready" ? load.palette : null}
          variant={variant}
          labels={labels}
          onPlayback={setPb}
        />
        {teacher && !observer && videoApi && (
          <ReplayVideoPanel
            api={videoApi}
            replayId={info.replayId}
            onError={(e) => toasts.push("error", t("video.downloadFailed"), toApiError(e).code)}
            onDone={(text) => toasts.push("info", text)}
          />
        )}
      </div>
      <ToastHost items={toasts.items} onDismiss={toasts.dismiss} pico={false} />
    </ScreenRoot>
  );
}

/** 교사·학생 재생 상단 바(제목 · 교사 칩 권위 리플레이 · 해시) */
function RoomReplayTop({
  kind,
  course,
  teacher,
  mismatch,
  playback,
  build,
}: {
  kind: string;
  course: string | null;
  teacher: boolean;
  mismatch: number | null;
  playback: "input" | "server";
  build: boolean;
}) {
  const { t } = useI18n();
  const titleKey = kind === "carry" ? "replay.room.carryTitle" : "replay.room.relayTitle";
  useTopBar({
    icon: "🎬",
    title: course ? t(`${titleKey}Of`, { course }) : t(titleKey),
    chips: teacher
      ? [
          { label: t("replay.room.chipAuthority"), testId: "replay-chip-authority" },
          {
            label: t("replay.teacher.hashChip", {
              // 빌드가 달라 재생 전에 넘어갔으면 이 기기에서 해시를 보지 않았다(—)
              hash: mismatch != null ? "✕" : build ? "—" : "✓",
              mode: t(playback === "server" ? "replay.mode.server" : "replay.mode.input"),
            }),
            testId: "replay-chip-hash",
          },
        ]
      : [],
  });
  return null;
}

declare global {
  interface Window {
    __pfRoomReplay?: {
      state: () => { tick: number; duration: number; playing: boolean; speed: number };
    };
  }
}

/** 장면 + 구간 막대 + 재생 줄(재생기 하나 — 바뀌면 새로 그린다) */
function RoomPlayback({
  player,
  analysis,
  palette,
  variant,
  labels,
  onPlayback,
}: {
  player: FallbackPlayer;
  /** P3-S6 구간 분석(Worker · 메인 대체) — 없으면 재생기에서 바로 */
  analysis: RoomAnalysis | null;
  palette: PaletteLike | null;
  variant: PlaybackVariant;
  labels: MachineLabels;
  onPlayback?: (p: PlaybackInfo) => void;
}) {
  const teacher = variant === "teacher";
  const observer = variant === "observer";
  const { t } = useI18n();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const zoomViewRef = useRef<GalleryViewLike | null>(null); // 확대 · 축소가 걸 뷰(그리기 루프가 채운다)
  const labelRefs = useRef(new Map<number, HTMLSpanElement>());
  const duration = player.durationTicks;
  const [tick, setTick] = useState(player.tick);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState<Speed>(1);
  const [seeking, setSeeking] = useState(false);
  const [layoutV, setLayoutV] = useState(player.layoutVersion);
  const [timeline, setTimeline] = useState<RoomTimeline | null>(null);
  const [timelineErr, setTimelineErr] = useState(false);
  const [mismatch, setMismatch] = useState<number | null>(player.mismatch);
  const [playback, setPlayback] = useState<"input" | "server">(player.playback);
  const [server, setServer] = useState<ServerState>(player.server);
  const [buffering, setBuffering] = useState(false);
  const build = player.switchReason === "build";
  useEffect(() => {
    onPlayback?.({ mismatch, playback, server, build });
  }, [onPlayback, mismatch, playback, server, build]);
  const [view, setView] = useState<"loading" | "3d" | "plan">("loading");
  const playingRef = useRef(playing);
  playingRef.current = playing;
  const speedRef = useRef<number>(speed);
  speedRef.current = speed;
  const seekingRef = useRef(false);
  const seekAbort = useRef<AbortController | null>(null);

  // 릴레이 진행(구간 라벨 · 막대) — 별도 재시뮬 한 번(P3-S6 Web Worker · 못 쓰면 메인 스레드)
  useEffect(() => {
    if (player.mode.kind !== "relay") return;
    let alive = true;
    (analysis ? analysis.timeline() : player.relayTimeline())
      .then((tl) => alive && setTimeline(tl))
      .catch(() => alive && setTimelineErr(true));
    return () => {
      alive = false;
    };
  }, [player, analysis]);

  // 룸 메타(몸체 배치 · 구간) — 모형 교체(layoutVersion)마다 다시
  const meta: RoomMeta = useMemo(
    () =>
      expandMeta({
        roomId: player.header.roomId,
        mode: { ...player.mode, ...(player.sections ? { sections: player.sections } : {}) },
        me: { machine: null, role: teacher ? "teacher" : "student" },
        paused: false,
        members: [],
        ...(player.layout as unknown as Omit<
          Parameters<typeof expandMeta>[0],
          "roomId" | "mode" | "me" | "paused" | "members"
        >),
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [player, layoutV, teacher],
  );
  /** 기계 → 별명(교사 · 관찰자 — 학생 재생에는 이름이 없다 · DR-01). 구간 라벨의 이름은 교사만 */
  const nickOf = useMemo(() => {
    const m = new Map<number, string>();
    if (!teacher) return m;
    for (const [k, v] of labels) m.set(k, v.label);
    return m;
  }, [labels, teacher]);
  const stageMachine = useMemo(() => {
    const m = new Map<number, number>();
    for (const s of player.sections ?? []) m.set(s.index, s.machine);
    return m;
  }, [player]);
  const tlRef = useRef(timeline);
  tlRef.current = timeline;

  // ── 그리기 + 재생 루프 ──
  useEffect(() => {
    const canvas = canvasRef.current;
    zoomViewRef.current = null;
    if (!canvas) return;
    let alive = true;
    let handle: GalleryViewLike | null = null;
    let raf = 0;
    let last = performance.now();
    let acc = 0;
    let shownAt = 0;
    let tinted = "";
    const sections = sectionsOf(meta);
    const req: GalleryViewRequest = {
      bodies: meta.bodies as unknown as GalleryViewRequest["bodies"],
      exhibits: meta.machines.map((m) => ({
        owner: m.owner,
        glbUrl: m.glbUrl,
        appearance: m.appearance as GalleryViewRequest["exhibits"][number]["appearance"],
        palette,
      })),
      quality: "high",
      roomScene: ROOM_SCENE,
      // SR-PHY-02 — 재시뮬레이션 틱 사이를 누산기(acc) 비율로 보간한다(한 틱 표시 지연)
      interpolate: true,
    };
    const loop = (now: number) => {
      if (!alive) return;
      if (playingRef.current && !seekingRef.current) {
        acc = Math.min(acc + ((now - last) / 1000) * 60 * speedRef.current, MAX_BACKLOG_TICKS);
        const t0 = performance.now();
        while (acc >= 1 && performance.now() - t0 < STEP_BUDGET_MS) {
          acc--;
          if (!player.step()) {
            setPlaying(false);
            acc = 0;
            break;
          }
        }
      } else acc = 0;
      last = now;
      if (player.layoutVersion !== layoutV) {
        setLayoutV(player.layoutVersion);
        return; // 새 메타로 다시 싣는다
      }
      if (handle) {
        const transforms = player.transforms();
        // alpha = 누산기 / 틱(멈춤 · 감는 중이면 1 — 최신 상태에서 멈춘다)
        const alpha = playingRef.current && !seekingRef.current ? Math.min(1, acc) : 1;
        handle.update({ tick: player.tick, transforms, alpha });
        const tl = tlRef.current;
        const at = tl ? tl.stageAt(player.tick) : null;
        const stage = at && at.stage !== null ? at.stage : null;
        const f =
          stage !== null
            ? relayFocus(meta, sections, transforms, stage, stageMachine.get(stage) ?? null, false)
            : null;
        const v = f ?? OVERVIEW;
        handle.setViewpoint({ x: v.x, z: v.z }, v.back ? { back: v.back } : undefined);
        // 구간 색 — 끝난 칸만(실패는 표시하지 않는다 · 영상과 같은 원칙)
        if (tl) {
          const cells = relayBar(tl.events, sections.length, player.tick);
          const key = cells.join(",");
          if (key !== tinted) {
            tinted = key;
            handle.setSectionStatus?.(
              cells.map((c) => (c === "current" ? "running" : c === "waiting" ? "waiting" : c)),
            );
          }
        }
        for (const m of meta.machines) {
          const el = labelRefs.current.get(m.machine);
          const q = el ? handle.project?.(m.bodyStart, 0.3) : null;
          if (el) {
            el.style.display = q ? "" : "none";
            if (q) el.style.transform = `translate(${q.x}px, ${q.y}px)`;
          }
        }
      }
      if (now - shownAt >= 100) {
        shownAt = now;
        setTick(player.tick);
        if (player.mismatch !== null) setMismatch(player.mismatch);
        setPlayback(player.playback);
        setServer(player.server);
        setBuffering(player.buffering);
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    roomDeps()
      .createGalleryView(canvas, req)
      .then(async (h) => {
        if (!alive) return h.dispose();
        await h.ready;
        if (!alive) return h.dispose();
        handle = h;
        zoomViewRef.current = h;
        h.setActive(meta.machines.map((m) => m.owner));
        setView("3d");
      })
      .catch(() => alive && setView("plan"));
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
      zoomViewRef.current = null;
      handle?.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, palette, player]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    window.__pfRoomReplay = { state: () => ({ tick, duration, playing, speed }) };
  });

  const seek = useCallback(
    async (to: number) => {
      seekAbort.current?.abort();
      const ac = new AbortController();
      seekAbort.current = ac;
      seekingRef.current = true;
      setSeeking(true);
      try {
        await player.seek(Math.max(0, Math.min(duration, to)), { signal: ac.signal });
      } catch {
        /* 더 새 탐색이 이어받았다 */
      } finally {
        if (seekAbort.current === ac) {
          seekingRef.current = false;
          setSeeking(false);
          setTick(player.tick);
        }
      }
    },
    [player, duration],
  );
  useEffect(() => () => seekAbort.current?.abort(), []);

  const doPlay = () => {
    if (player.ended) void seek(0);
    setPlaying(true);
  };
  const frac = fraction(tick, duration);
  // 관찰자도 시간을 본다(와이어 sfs-01-05 '2분 30초' · sfs-14 '0:48 / 2:30')
  const times = observer || showTimes(teacher ? "teacher" : "student", false);
  const clock = t("replay.time", {
    now: formatClock(tickToS(tick), false),
    total: formatClock(tickToS(duration), false),
  });
  const n = player.sections?.length ?? 0;
  const at = timeline ? timeline.stageAt(tick) : null;
  const cells = timeline && n ? relayBar(timeline.events, n, tick) : null;
  const stageLabel = (() => {
    if (!at) return null;
    if (at.status === "setup") return t("replay.room.setup");
    if (at.status === "allDone" || at.stage === null) return t("replay.room.allDone");
    const who = teacher ? nickOf.get(stageMachine.get(at.stage) ?? -1) : undefined;
    return who
      ? t("replay.room.stageOf", { n: at.stage + 1, total: n, name: who })
      : t("replay.room.stage", { n: at.stage + 1, total: n });
  })();
  const ready = view !== "loading";

  return (
    <div className="pf-replay__left">
      <div
        className="pf-course__scene pf-replay__room"
        data-testid="room-replay-scene"
        data-view={view}
      >
        <canvas ref={canvasRef} className="pf-course__canvas" aria-hidden="true" />
        {view === "3d" && (
          <ZoomControls getView={() => zoomViewRef.current} wheelTarget={canvasRef} />
        )}
        {(teacher || observer) &&
          meta.machines.map((m) => {
            const l = labels.get(m.machine);
            if (!l) return null;
            const who = l.label;
            return (
              <span
                key={m.owner}
                className={`pf-course__tag pf-replay__tag${l.mine ? " pf-replay__tag--mine" : ""}`}
                data-machine={m.machine}
                data-mine={l.mine ? "" : undefined}
                ref={(el) => {
                  if (el) labelRefs.current.set(m.machine, el);
                  else labelRefs.current.delete(m.machine);
                }}
              >
                {who}
              </span>
            );
          })}
        <div className="pf-hud pf-hud--timer" data-testid="replay-time">
          {[stageLabel, times ? clock : null].filter(Boolean).join(" · ") ||
            t("replay.percent", { n: Math.round(frac * 100) })}
        </div>
      </div>
      {cells && !observer && (
        <ol
          className="pf-relay__bar pf-replay__stages"
          aria-label={t("replay.room.barLabel")}
          data-testid="replay-relay-bar"
        >
          {cells.map((c, i) => {
            const start = stageStartTick(timeline!.events, i);
            const who = teacher ? nickOf.get(stageMachine.get(i) ?? -1) : undefined;
            return (
              <li key={i}>
                <button
                  type="button"
                  className={`pf-relay__tick pf-replay__stage pf-relay__tick--${c === "current" ? "cur" : c}`}
                  data-cell={c}
                  aria-current={c === "current" ? "step" : undefined}
                  aria-label={
                    who
                      ? t("replay.room.goStageOf", { n: i + 1, name: who })
                      : t("replay.room.goStage", { n: i + 1 })
                  }
                  title={who}
                  disabled={start === null || !ready}
                  onClick={() => start !== null && void seek(start)}
                >
                  {i + 1}
                </button>
              </li>
            );
          })}
        </ol>
      )}
      <div className="pf-pane pf-replay__controls" data-testid="replay-controls">
        <div className="pf-replay__row">
          <Button
            variant="primary"
            className="pf-replay__btn"
            aria-label={t("replay.play")}
            aria-pressed={playing}
            disabled={!ready || playing}
            onClick={doPlay}
            data-testid="replay-play"
          >
            ▶
          </Button>
          <Button
            variant="ghost"
            className="pf-replay__btn"
            aria-label={t("replay.pause")}
            disabled={!ready || !playing}
            onClick={() => setPlaying(false)}
            data-testid="replay-pause"
          >
            ⏸
          </Button>
          <div
            className="pf-replay__timeline"
            role={observer ? "progressbar" : "slider"}
            tabIndex={observer ? undefined : 0}
            aria-label={t("replay.timeline")}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(frac * 100)}
            aria-valuetext={times ? clock : t("replay.percent", { n: Math.round(frac * 100) })}
            aria-disabled={!ready}
            aria-busy={seeking}
            data-testid="replay-timeline"
            onPointerDown={(e) => {
              // 관찰자 변형은 재생 · 일시정지 · 배속만(5-9) — 막대는 보기만
              if (!ready || observer) return;
              const r = e.currentTarget.getBoundingClientRect();
              void seek(tickAt((e.clientX - r.left) / Math.max(1, r.width), duration));
            }}
            onKeyDown={(e) => {
              if (!ready || observer) return;
              const to = keySeek(e.key, tick, duration);
              if (to == null) return;
              e.preventDefault();
              void seek(to);
            }}
          >
            <span className="pf-replay__bar">
              <span className="pf-replay__fill" style={{ width: `${frac * 100}%` }} />
            </span>
          </div>
          <div className="pf-replay__speeds" role="radiogroup" aria-label={t("replay.speed")}>
            {SPEEDS.map((s) => (
              <Button
                key={s}
                role="radio"
                aria-checked={speed === s}
                variant={speed === s ? "accent" : "ghost"}
                className="pf-replay__btn"
                disabled={!ready}
                onClick={() => setSpeed(s)}
                data-speed={s}
              >
                {`${s}×`}
              </Button>
            ))}
          </div>
        </div>
        {!observer && (
          <ul
            className="pf-replay__state"
            aria-label={t("replay.stateLabel")}
            data-testid="replay-state"
          >
            {(teacher || playback === "input") && (
              <li
                className={`pf-replay__chip${playback === "input" ? " pf-replay__chip--on" : ""}`}
                data-mode="input"
                aria-current={playback === "input" ? "true" : undefined}
              >
                {t(teacher ? "replay.state.inputTeacher" : "replay.state.input")}
              </li>
            )}
            {teacher && (
              <li className="pf-replay__chip" data-mode="authority">
                {t("replay.room.authorityState")}
              </li>
            )}
            {/* 서버 재생 — 교사만 칩(SCR FS-14-01) · 학생은 아래 '다시 맞춰서 재생해요' 안내만 */}
            {teacher && (
              <li
                className={`pf-replay__chip${playback === "server" ? " pf-replay__chip--on" : " pf-replay__chip--ph2"}`}
                data-mode="server"
                aria-current={playback === "server" ? "true" : undefined}
              >
                {t("replay.state.server")}
              </li>
            )}
          </ul>
        )}
        {seeking && (
          <p className="pf-muted" role="status" data-testid="replay-seeking">
            {t("replay.seeking")}
          </p>
        )}
        {buffering && (
          <p className="pf-muted" role="status" data-testid="replay-buffering">
            {t("replay.room.serverBuffering")}
          </p>
        )}
        {teacher && build && mismatch === null && (
          <p
            className="pf-replay__note"
            role="status"
            data-testid="replay-build"
            data-server={server}
          >
            <span aria-hidden="true">{playback === "server" ? "🔄 " : "⚠ "}</span>
            {playback === "server"
              ? t("replay.room.buildNext")
              : `${t("replay.room.buildInput")} ${t("replay.room.serverFailed")}`}
          </p>
        )}
        {teacher && mismatch !== null && (
          <p
            className="pf-replay__note"
            role="status"
            data-testid="replay-fe501"
            data-server={server}
          >
            <span aria-hidden="true">{playback === "server" ? "🔄 " : "⚠ "}</span>
            {`${t("fe.FE-501.teacher.cause")} — ${
              playback === "server"
                ? t("replay.room.serverNext", { s: formatClock(tickToS(mismatch), false) })
                : server === "failed"
                  ? `${t("replay.room.mismatchNext", { s: formatClock(tickToS(mismatch), false) })} ${t("replay.room.serverFailed")}`
                  : t("replay.room.mismatchNext", { s: formatClock(tickToS(mismatch), false) })
            } (${t("common.codeLabel", { code: "FE-501" })})`}
          </p>
        )}
        {!teacher && !observer && playback === "server" && (
          <p className="pf-replay__note" role="status" data-testid="replay-fe501">
            <span aria-hidden="true">🔄 </span>
            {t("fe.FE-501.student.cause")}
          </p>
        )}
        {teacher && timelineErr && (
          <p className="pf-muted" role="status" data-testid="replay-timeline-err">
            {t("replay.room.timelineErr")}
          </p>
        )}
      </div>
    </div>
  );
}
